import { AppWindow, Link2, MoreHorizontal, Plus, Sparkles } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";

import { StatusPill } from "@/components/dashboard/IntegrationCardShell";
import { cn } from "@/lib/utils";
import { STUDIO_NAME, deriveUpdatedLabel } from "@domain/studio-app";

import type { StudioAppListItem } from "@/hooks/useStudioApps";

/**
 * The Studio library — the mockup's grid/AppCard/MiniAppThumb/empty-state
 * JSX preserved verbatim, data inverted to props (studio spec §4.4): the
 * library route fetches via `useStudioApps` and passes the rows down. The
 * `framework` chip became a `fileCount` chip; "Updated …" derives from the
 * GCS mtime via `deriveUpdatedLabel`.
 */

/**
 * Live mini preview used as a card/peek thumbnail: the real app HTML in a
 * scaled-down, non-interactive iframe (same sandbox rule as the full view).
 * `html` is the list row's inline entry body — null (over the inline cap)
 * renders an honest blank card face.
 */
export function MiniAppThumb({ html, className }: { html: string | null; className?: string }) {
  return (
    <div className={cn("pointer-events-none relative overflow-hidden bg-white", className)} aria-hidden>
      {html !== null && (
        <iframe
          srcDoc={html}
          title=""
          tabIndex={-1}
          sandbox="allow-scripts"
          referrerPolicy="no-referrer"
          loading="lazy"
          className="absolute top-0 left-0 h-[800px] w-[1000px] origin-top-left scale-[0.28] border-0"
        />
      )}
    </div>
  );
}

/** "Updated …" from the row's mtime; blank when the mtime is unknown. */
function updatedLabelOf(updatedAt: number | null): string {
  return updatedAt === null ? "" : deriveUpdatedLabel({ updatedAt, now: Date.now() });
}

function AppCard({
  app,
  onOpen,
  onShare,
}: {
  app: StudioAppListItem;
  onOpen: () => void;
  onShare: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const isShared = app.share !== null && app.share.active;

  return (
    <div className="relative flex flex-col overflow-hidden rounded-3xl border border-home-ink/8 bg-white text-left shadow-[0_16px_40px_-26px_rgba(33,31,28,0.4)] transition-transform hover:-translate-y-0.5 dark:border-white/8 dark:bg-surface-dark-card">
      <button type="button" onClick={onOpen} className="text-left">
        <MiniAppThumb html={app.entryHtml} className="h-36 w-full border-b border-home-ink/8 dark:border-white/8" />
      </button>
      <div className="flex flex-1 flex-col p-4">
        <div className="flex items-start justify-between gap-2">
          <button type="button" onClick={onOpen} className="min-w-0 text-left">
            <h3 className="truncate font-display text-[16px] font-semibold tracking-tight text-home-ink dark:text-white">
              {app.name}
            </h3>
          </button>
          {isShared ? (
            <StatusPill variant="active">Shared</StatusPill>
          ) : (
            <StatusPill variant="muted">Private</StatusPill>
          )}
        </div>
        <div className="mt-auto flex items-center gap-2 pt-3 font-body text-[12px] text-home-ink/55 dark:text-white/55">
          <span className="rounded-full bg-home-ink/[0.06] px-2 py-0.5 font-semibold dark:bg-white/10">
            {app.fileCount} {app.fileCount === 1 ? "file" : "files"}
          </span>
          <span>{updatedLabelOf(app.updatedAt)}</span>
          <div className="relative ml-auto">
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="App actions"
              className="flex h-7 w-7 items-center justify-center rounded-full text-home-ink/50 transition-colors hover:bg-black/5 dark:text-white/50 dark:hover:bg-white/10"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
            <AnimatePresence>
              {menuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 4 }}
                  className="absolute right-0 bottom-9 z-10 w-36 overflow-hidden rounded-2xl border border-black/8 bg-white py-1 shadow-[0_16px_40px_-20px_rgba(33,31,28,0.45)] dark:border-white/10 dark:bg-surface-dark-card"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onShare();
                    }}
                    className="flex w-full items-center gap-2 px-3.5 py-2 font-body text-[13px] font-semibold text-home-ink/75 transition-colors hover:bg-black/5 dark:text-white/75 dark:hover:bg-white/10"
                  >
                    <Link2 className="h-3.5 w-3.5" strokeWidth={2.2} />
                    Share
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onOpen();
                    }}
                    className="flex w-full items-center gap-2 px-3.5 py-2 font-body text-[13px] font-semibold text-home-ink/75 transition-colors hover:bg-black/5 dark:text-white/75 dark:hover:bg-white/10"
                  >
                    <AppWindow className="h-3.5 w-3.5" strokeWidth={2.2} />
                    Open
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}

/** First-run empty state — a ghost app card plus example asks. */
export function StudioEmptyState({
  onCreate,
  exampleAsks,
}: {
  onCreate?: () => void;
  /** Seed asks (the chat starters) — one click starts a build from that ask. */
  exampleAsks: readonly string[];
}) {
  return (
    <div className="mx-auto flex max-w-md flex-col items-center px-4 py-16 text-center">
      {/* Ghost app card — the shape of an app, waiting to exist */}
      <div className="relative rotate-[-2deg] opacity-70" aria-hidden>
        <div className="w-[220px] overflow-hidden rounded-2xl border border-dashed border-home-ink/20 bg-white/60 dark:border-white/20 dark:bg-white/[0.04]">
          <div className="h-20 border-b border-dashed border-home-ink/15 bg-home-ink/[0.04] dark:border-white/15 dark:bg-white/[0.06]" />
          <div className="flex items-center justify-between p-3">
            <span className="h-2 w-20 rounded-full bg-home-ink/15 dark:bg-white/15" />
            <span className="h-3.5 w-10 rounded-full bg-home-ink/[0.08] dark:bg-white/10" />
          </div>
        </div>
        <span className="absolute -top-3 -right-7 flex h-9 w-9 rotate-[8deg] items-center justify-center rounded-2xl bg-home-yellow shadow-[0_8px_20px_-8px_rgba(33,31,28,0.45)]">
          <AppWindow className="h-4.5 w-4.5 text-home-ink" strokeWidth={2.2} />
        </span>
      </div>

      <h2 className="mt-8 font-display text-[22px] font-semibold tracking-tight text-home-ink dark:text-white">
        Nothing built yet
      </h2>
      <p className="mt-2 max-w-sm font-body text-[14px] leading-relaxed text-home-ink/60 dark:text-white/60">
        Describe an app in plain words. Your agent builds it — with source you
        can read and own — and it lives here.
      </p>

      <button
        type="button"
        onClick={onCreate}
        className="mt-6 inline-flex min-h-[46px] items-center gap-2 rounded-full bg-home-yellow px-6 font-body text-[14px] font-bold text-home-ink shadow-[0_10px_28px_-12px_rgba(33,31,28,0.4)] transition-transform hover:scale-[1.02] active:scale-[0.98]"
      >
        <Plus className="h-[18px] w-[18px]" strokeWidth={2.6} />
        Build your first app
      </button>

      <p className="mt-6 font-body text-[12px] font-medium text-home-ink/45 dark:text-white/45">
        or start from an ask like these
      </p>
      <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
        {exampleAsks.map((ask) => (
          <button
            key={ask}
            type="button"
            onClick={onCreate}
            className="inline-flex min-h-[38px] items-center gap-1.5 rounded-full border border-home-ink/15 px-4 font-body text-[13px] font-medium text-home-ink/70 transition-all hover:-translate-y-0.5 hover:border-brand-coral/50 hover:text-brand-coral-dark dark:border-white/15 dark:text-white/70 dark:hover:border-brand-coral/60 dark:hover:text-brand-coral"
          >
            <Sparkles className="h-3.5 w-3.5 shrink-0 text-brand-coral/70" strokeWidth={2.2} />
            {ask}
          </button>
        ))}
      </div>
    </div>
  );
}

export interface StudioLibraryProps {
  apps: StudioAppListItem[];
  isLoading: boolean;
  /** Start a new build (stamped thread → gestation route). */
  onNewApp: () => void;
  /** Open an app's detail surface (`/studio/$appDir`). */
  onOpenApp: (appDir: string) => void;
  /** Seed asks shown in the empty state (the chat starters). */
  exampleAsks: readonly string[];
}

/**
 * The Studio surface: library grid of live app cards. Opening an app is a
 * navigation (`onOpenApp` → the detail route) — the mockup's in-place
 * open-state became `StudioAppDetail` behind `/studio/$appDir`.
 */
export function StudioLibrary({ apps, isLoading, onNewApp, onOpenApp, exampleAsks }: StudioLibraryProps) {
  if (!isLoading && apps.length === 0) {
    return <StudioEmptyState onCreate={onNewApp} exampleAsks={exampleAsks} />;
  }

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-6 flex items-center gap-3">
        <div>
          <h1 className="font-display text-[24px] font-semibold tracking-tight text-home-ink dark:text-white">
            {STUDIO_NAME}
          </h1>
          <p className="mt-0.5 font-body text-[13px] text-home-ink/55 dark:text-white/55">
            {apps.length} apps · built with your agent, owned by you
          </p>
        </div>
        <button
          type="button"
          onClick={onNewApp}
          className="ml-auto inline-flex min-h-[42px] items-center gap-2 rounded-full bg-home-yellow px-5 font-body text-[13px] font-bold text-home-ink shadow-[0_10px_28px_-12px_rgba(33,31,28,0.4)] transition-transform hover:scale-[1.02] active:scale-[0.98]"
        >
          <Plus className="h-4 w-4" strokeWidth={2.6} />
          New app
        </button>
      </div>

      <div className="grid gap-5 [grid-template-columns:repeat(auto-fill,minmax(min(260px,100%),1fr))]">
        {apps.map((app) => (
          <AppCard
            key={app.appDir}
            app={app}
            onOpen={() => onOpenApp(app.appDir)}
            /* Card-menu Share routes through the same gated flow as the
               toolbar: open the app; the Share button there owns the gate. */
            onShare={() => onOpenApp(app.appDir)}
          />
        ))}
      </div>
    </div>
  );
}
